import { color } from 'd3-color';
import { patterns } from '@/utils/patterns';

import {
  // sequential - single hue
  interpolateBlues,
  interpolateGreens,
  interpolateGreys,
  interpolateOranges,
  interpolatePurples,
  interpolateReds,
  // sequential - multi hue
  interpolateBuGn,
  interpolateBuPu,
  interpolateGnBu,
  interpolateOrRd,
  interpolatePuBuGn,
  interpolatePuBu,
  interpolatePuRd,
  interpolateRdPu,
  interpolateYlGnBu,
  interpolateYlGn,
  interpolateYlOrBr,
  interpolateYlOrRd,
  // diverging
  interpolateBrBG,
  interpolatePRGn,
  interpolatePiYG,
  interpolatePuOr,
  interpolateRdBu,
  interpolateRdGy,
  interpolateRdYlBu,
  interpolateRdYlGn,
  interpolateSpectral,
  // categorical/qualitative
  schemeAccent,
  schemeDark2,
  schemePaired,
  schemePastel1,
  schemePastel2,
  schemeSet1,
  schemeSet2,
  schemeSet3,
  schemeTableau10,
} from 'd3-scale-chromatic';

const brewer_palettes = {
  // sequential single-hue
  interpolateBlues: interpolateBlues,
  interpolateGreens: interpolateGreens,
  interpolateGreys: interpolateGreys,
  interpolateOranges: interpolateOranges,
  interpolatePurples: interpolatePurples,
  interpolateReds: interpolateReds,

  // sequential multi-hue
  interpolateBuGn: interpolateBuGn,
  interpolateBuPu: interpolateBuPu,
  interpolateGnBu: interpolateGnBu,
  interpolateOrRd: interpolateOrRd,
  interpolatePuBuGn: interpolatePuBuGn,
  interpolatePuBu: interpolatePuBu,
  interpolatePuRd: interpolatePuRd,
  interpolateRdPu: interpolateRdPu,
  interpolateYlGnBu: interpolateYlGnBu,
  interpolateYlGn: interpolateYlGn,
  interpolateYlOrBr: interpolateYlOrBr,
  interpolateYlOrRd: interpolateYlOrRd,

  // diverging
  interpolateBrBG: interpolateBrBG,
  interpolatePRGn: interpolatePRGn,
  interpolatePiYG: interpolatePiYG,
  interpolatePuOr: interpolatePuOr,
  interpolateRdBu: interpolateRdBu,
  interpolateRdGy: interpolateRdGy,
  interpolateRdYlBu: interpolateRdYlBu,
  interpolateRdYlGn: interpolateRdYlGn,
  interpolateSpectral: interpolateSpectral,

  // categorical
  schemeAccent: schemeAccent,
  schemeDark2: schemeDark2,
  schemePaired: schemePaired,
  schemePastel1: schemePastel1,
  schemePastel2: schemePastel2,
  schemeSet1: schemeSet1,
  schemeSet2: schemeSet2,
  schemeSet3: schemeSet3,
  schemeTableau10: schemeTableau10,
};

export function getBrewerPalettes() {
  return {
    'sequential-single': [
      'Blues',
      'Greens',
      'Greys',
      'Oranges',
      'Purples',
      'Reds',
    ],
    'sequential-multi': [
      'BuGn',
      'BuPu',
      'GnBu',
      'OrRd',
      'PuBuGn',
      'PuBu',
      'PuRd',
      'RdPu',
      'YlGnBu',
      'YlGn',
      'YlOrBr',
      'YlOrRd',
    ],
    diverging: [
      'BrBG',
      'PRGn',
      'PiYG',
      'PuOr',
      'RdBu',
      'RdGy',
      'RdYlBu',
      'RdYlGn',
      'Spectral',
    ],
    categorical: [
      'Accent',
      'Dark2',
      'Paired',
      'Pastel1',
      'Pastel2',
      'Set1',
      'Set2',
      'Set3',
      'Tableau10',
    ],
  };
}

export function getBrewerColor(name, n_classes, i) {
  let clr = null;

  // qualitative palette
  if (name.match(/^(Accent|Dark2|Paired|Pastel\d|Set\d|Tableau10)$/)) {
    const scheme = brewer_palettes['scheme' + name];
    clr = color(scheme[i]);
  }
  // continuous
  else {
    let step = 1.0 / (n_classes + 1);
    const interpolate = brewer_palettes['interpolate' + name];
    clr = color(interpolate((i + 1) * step));
  }

  return clr != null ? clr.formatRgb() : 'rgb(200,200,200)';
}

// return qualitative palettes that have at least as many colors as the number of cohorts
export function getBrewerQualitativePalettes(n_classes) {
  let qps = [
    'Accent',
    'Dark2',
    'Paired',
    'Pastel1',
    'Pastel2',
    'Set1',
    'Set2',
    'Set3',
    'Tableau10',
  ];
  let palettes = [];
  qps.forEach(qp => {
    let nc = brewer_palettes['scheme' + qp].length;
    if (nc >= n_classes) {
      palettes.push(qp);
    }
  });
  return palettes;
}

// grey/light blue
export const colors = {
  cohort: '#3F51B5',
  'cohort-circle-fill': '#1B33B5',
  'cohort-circle-fill-opacity': '0.8',
  'cohort-circle-stroke': '#769eb5',
  'cohort-circle-stroke-width': '2',

  population: '#B8D6FF',
  'population-circle-fill': '#85B9FF',
  'population-circle-fill-opacity': '0.8',
  'population-circle-stroke': '#E1FFE1',
  'population-circle-stroke-width': '2',

  nonCohort: '#3FB551',

  'warn-background': '#F83008',
  'warn-text': '#F8D5F0',

  firstVisit: '#8bdb35',
  'firstVisit-opacity': '0.4',
  lastVisit: '#db5454',
  'lastVisit-opacity': '0.4',

  pvals: {
    // Brewer 5 class single hue sequential (blue)
    '1-brewer5': { color: '#FFFFFF', class: 'pval-lt-1-brewer5' },
    '0.1-brewer5': { color: '#BDD7E7', class: 'pval-lt-0p1-brewer5' },
    '0.05-brewer5': { color: '#6BAED6', class: 'pval-lt-0p05-brewer5' },
    '0.01-brewer5': { color: '#3182BD', class: 'pval-lt-0p01-brewer5' },
    '0.001-brewer5': { color: '#08519C', class: 'pval-lt-0p001-brewer5' },

    '1-brewer5v2': { color: '#FFFFFF', class: 'pval-lt-1-brewer5v2' },
    '0.1-brewer5v2': { color: '#EFF3FF', class: 'pval-lt-0p1-brewer5v2' },
    '0.05-brewer5v2': { color: '#BDD7E7', class: 'pval-lt-0p05-brewer5v2' },
    '0.01-brewer5v2': { color: '#6BAED6', class: 'pval-lt-0p01-brewer5v2' },
    '0.001-brewer5v2': { color: '#3182BD', class: 'pval-lt-0p001-brewer5v2' },

    // Brewer 6 class single hue sequential (blue)
    '1-brewer6': { color: '#FFFFFF', class: 'pval-lt-1-brewer6' },
    '0.1-brewer6': { color: '#C6DBEF', class: 'pval-lt-0p1-brewer6' },
    '0.05-brewer6': { color: '#9ECAE1', class: 'pval-lt-0p05-brewer6' },
    '0.01-brewer6': { color: '#6BAED6', class: 'pval-lt-0p01-brewer6' },
    '0.001-brewer6': { color: '#3182BD', class: 'pval-lt-0p001-brewer6' },

    // Brewer 6 class single hue sequential (red)
    '1-brewer6r': { color: '#FFFFFF', class: 'pval-lt-1-brewer6r' },
    '0.1-brewer6r': { color: '#FCBBA1', class: 'pval-lt-0p1-brewer6r' },
    '0.05-brewer6r': { color: '#FC9272', class: 'pval-lt-0p05-brewer6r' },
    '0.01-brewer6r': { color: '#FB6A4A', class: 'pval-lt-0p01-brewer6r' },
    '0.001-brewer6r': { color: '#DE2D26', class: 'pval-lt-0p001-brewer6r' },

    // Brewer 4 class single hue sequential (purple)
    '1-brewer4p': { color: '#FFFFFF', class: 'pval-lt-1-brewer4p' },
    '0.1-brewer4p': { color: '#F2F0F7', class: 'pval-lt-0p1-brewer4p' },
    '0.05-brewer4p': { color: '#CBC9E2', class: 'pval-lt-0p05-brewer4p' },
    '0.01-brewer4p': { color: '#9E9AC8', class: 'pval-lt-0p01-brewer4p' },
    '0.001-brewer4p': { color: '#6A51A3', class: 'pval-lt-0p001-brewer4p' },

    // Brewer 5 class single hue sequential (purple)
    '1-brewer5p': { color: '#FFFFFF', class: 'pval-lt-1-brewer5p' },
    '0.1-brewer5p': { color: '#F2F0F7', class: 'pval-lt-0p1-brewer5p' },
    '0.05-brewer5p': { color: '#CBC9E2', class: 'pval-lt-0p05-brewer5p' },
    '0.01-brewer5p': { color: '#9E9AC8', class: 'pval-lt-0p01-brewer5p' },
    '0.001-brewer5p': { color: '#756BB1', class: 'pval-lt-0p001-brewer5p' },

    // Trying again with the value cranked up to 100
    '1-val100': { color: '#FFFFFF', class: 'pval-lt-1-val100' }, // sat = 25
    '0.1-val100': { color: '#A6B3FF', class: 'pval-lt-0p1-val100' }, // sat = 35
    '0.05-val100': { color: '#8C9EFF', class: 'pval-lt-0p05-val100' }, // sat = 45
    '0.01-val100': { color: '#7388FF', class: 'pval-lt-0p01-val100' }, // sat = 55
    '0.001-val100': { color: '#5963FF', class: 'pval-lt-0p001-val100' }, // sat = 65

    // Trying again with the value at 80
    '1-val80': { color: '#FFFFFF', class: 'pval-lt-1-val80' }, // sat = 21
    '0.1-val80': { color: '#8C97CF', class: 'pval-lt-0p1-val80' }, // sat = 32
    '0.05-val80': { color: '#7683CF', class: 'pval-lt-0p05-val80' }, // sat = 43
    '0.01-val80': { color: '#5F70CF', class: 'pval-lt-0p01-val80' }, // sat = 54
    '0.001-val80': { color: '#485CCF', class: 'pval-lt-0p001-val80' }, // sat = 65
  },

  // Brewer 9 class qualitative - darker colors
  cohorts: [
    '#e41a1c',
    '#cab2d6', // substituted for blue / #377eb8
    '#4daf4a',
    '#984ea3',
    '#ff7f00',
    '#33ff33',
    '#a65628',
    '#fdbf6f', // substituted for pink / #f781bf
    '#999999',
  ],

  // Brewer 9 class qualitative - pastel colors with yellow moved to the end
  bar_graphs: [
    '#8dd3c7',
    '#bebada',
    '#fb8072',
    '#80b1d3', // light blue
    '#fdb462',
    '#b3de69',
    '#fccde5', // light pink
    '#d9d9d9',
    '#ffffb3',
  ],

  plotly_palette: [
    '#1f77b4', // muted blue
    '#ff7f0e', // safety orange
    '#2ca02c', // cooked asparagus green
    '#d62728', // brick red
    '#9467bd', // muted purple
    '#8c564b', // chestnut brown
    '#e377c2', // raspberry yogurt pink
    '#7f7f7f', // middle gray
    '#bcbd22', // curry yellow-green
    '#17becf', // blue-teal
  ],
};

export function getNumDatasetsColor(nd) {
  if (nd < 1) {
    return colors['warn-background'];
  } else {
    return colors['population'];
  }
}

export function getNumDatasetsTextColor(nd) {
  if (nd < 1) {
    return colors['warn-text'];
  } else {
    return '#273CDC';
  }
}

export function getNumSubjectsColor(nSubjects) {
  if (nSubjects <= 25) {
    return colors['warn-background'];
  } else {
    return colors['population'];
  }
}

export function getNumSubjectsTextColor(nSubjects) {
  if (nSubjects <= 25) {
    return colors['warn-text'];
  } else {
    return '#273CDC';
  }
}

export function getNumVariablesColor(nVars) {
  if (nVars === 0) {
    return colors['warn-background'];
  } else {
    return colors['population'];
  }
}

export function getNumVariablesTextColor(nVars) {
  if (nVars === 0) {
    return colors['warn-text'];
  } else {
    return '#273CDC';
  }
}

export function hexToRgbA(hex) {
  var c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length == 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');
    return [(c >> 16) & 255, (c >> 8) & 255, c & 255];
  }
  throw new Error('Bad Hex');
}

// color palettes for male/female cohorts - these will be checked in order
const palettes = [
  {
    regex: new RegExp('female|sex\\s+-\\s+f', 'i'),
    colors: ['#f781bf', '#fddaec', '#fb9a99', '#fccde5'],
    ind: 0,
  },
  {
    regex: new RegExp('male|sex\\s+-\\s+m', 'i'),
    colors: ['#1f78b4', '#80b1d3', '#b3cde3', '#a6cee3'],
    ind: 0,
  },
];

// Assign colors/patterns to study groups that do not already have a color assignment.
export function assignStudyGroupColors(studyGroups, collectionId) {
  // cohort color/pattern updates to save
  let cohortUpdates = [];

  // index fill patterns
  let pdict = {};
  patterns.forEach(p => {
    pdict[p['id']] = p;
  });
  studyGroups
    .filter(v => v.collection_id === collectionId)
    .forEach(c => {
      if ('pattern' in c && c.pattern != null && c.pattern in pdict) {
        c.pattern = pdict[c.pattern];
      }
    });

  // compute cohort signature based on its predictor variables
  let computeCohortSig = function(c) {
    const queries = c.queries;
    let keys = {};
    let hasCategorical = false;
    let hasFlipAxis = false;

    queries.forEach(q => {
      if ('subject_ontology' in q.input_variable) {
        keys[
          q.input_variable.subject_ontology.abbreviation +
            '|' +
            q.input_variable.dimension_label
        ] = 1;
        if (q.input_variable.subject_ontology.data_category == 'Categorical') {
          hasCategorical = true;
        } else if (q.input_variable.subject_ontology.flip_axis) {
          hasFlipAxis = true;
        }
      } else if ('observation_ontology' in q.input_variable) {
        keys[
          q.input_variable.observation_ontology.abbreviation +
            '|' +
            q.input_variable.dimension_label
        ] = 1;
        if (
          q.input_variable.observation_ontology.data_category == 'Categorical'
        ) {
          hasCategorical = true;
        } else if (q.input_variable.observation_ontology.flip_axis) {
          hasFlipAxis = true;
        }
      }
    });
    let qsig = Object.keys(keys)
      .sort()
      .join(',');
    return {
      sig: qsig,
      hasCategorical: hasCategorical,
      hasFlipAxis: hasFlipAxis,
    };
  };
  // end computeCohortSig

  // colors already used
  let colorsUsed = {};
  // cohorts with no color assigned after 1st pass
  let noColorCohorts = [];
  // mapping from cohort to predictor variable signature
  let cohortPredVarSig = {};
  // mapping from cohort to whether one of its queries involves a categorical var
  let cohortHasCategorical = {};
  // whether any cohort is based on a continuous variable with a flipped axis
  let cohortHasFlipAxis = {};

  studyGroups
    .filter(v => v.collection_id === collectionId)
    .forEach(c => {
      let hasColor = 'color' in c && c.color != null;
      if (hasColor) {
        colorsUsed[c.color] = 1;
      } else {
        // check against hard-coded color palettes, even if color already assigned
        var matched = false;
        palettes.forEach(p => {
          if (!matched && c.label.match(p['regex'])) {
            var nc = p['colors'].length;
            let new_color = p['colors'][p['ind'] % nc];
            p['ind'] += 1;
            if (new_color != c.color) {
              c.color = new_color;
              c.pattern = 'solid';
              matched = true;
              colorsUsed[c.color] = 1;
              cohortUpdates.push({
                cohort_id: c.id,
                color: c.color,
                pattern: c.pattern,
              });
              hasColor = true;
            }
          }
        });
      }

      // cohorts with no prior color assignment
      if (!hasColor) {
        noColorCohorts.push(c);
      }
      let cs = computeCohortSig(c);
      cohortPredVarSig[c.id] = cs['sig'];
      cohortHasCategorical[c.id] = cs['hasCategorical'];
      cohortHasFlipAxis[c.id] = cs['hasFlipAxis'];
    });

  // group no color cohorts by signature
  let sigToNCC = {};
  noColorCohorts.forEach(c => {
    let sig = cohortPredVarSig[c.id];
    if (!(sig in sigToNCC)) {
      sigToNCC[sig] = [];
    }
    sigToNCC[sig].push(c);
  });

  let bp = getBrewerPalettes();

  // continuous palettes
  // skip blues and pinks to avoid collision with hard-coded palettes for sex
  let con_palettes = [
    ...bp['sequential-single'],
    ...bp['sequential-multi'],
  ].filter(p => !p.match(/Blues|RdPu|Bu/));
  let conp = {
    palettes: con_palettes,
    ind: 0,
    len: con_palettes.length,
    all_used: false,
    continuous: true,
  };

  // categorical palettes
  let cat_palettes = [...bp['categorical']];
  let catp = {
    palettes: cat_palettes,
    ind: 0,
    len: cat_palettes.length,
    all_used: false,
    continuous: false,
  };

  let getNextUnusedPalette = function(ph, n_cohorts) {
    let ind = ph['ind'];
    let palette = null;

    // search for a palette with no previously-used colors
    if (ind < ph['len']) {
      while (ind < ph['len']) {
        let candidate = ph['palettes'][ind];
        // check for collisions
        let candidate_colors_used = false;

        // continuous palette - check a range of possible study group sizes
        if (ph['continuous']) {
          let s = 2;
          while (s <= 10) {
            let pc = getBrewerColor(candidate, s, 0);
            if (pc in colorsUsed) {
              candidate_colors_used = true;
              break;
            }
            s += 1;
          }
        } else {
          let pc = getBrewerColor(candidate, n_cohorts, 0);
          if (pc in colorsUsed) candidate_colors_used = true;
        }
        if (!candidate_colors_used) {
          palette = candidate;
          break;
        }
        ind += 1;
      }
    }

    if (palette == null) ph['all_used'] = true;

    if (ph['all_used']) {
      let mod_ind = ind % ph['len'];
      ph['ind'] += 1;
      return ph['palettes'][mod_ind];
    }

    return palette;
  };

  // assign each group of cohorts a color palette
  Object.keys(sigToNCC).forEach(sig => {
    let cohorts = sigToNCC[sig];
    let nc = cohorts.length;
    let hasCategorical = cohortHasCategorical[cohorts[0].id];
    let ph = hasCategorical ? catp : conp;

    let palette = getNextUnusedPalette(ph, nc);
    let hasFlipAxis = cohortHasFlipAxis[cohorts[0].id];
    let ind = hasFlipAxis ? nc : 0;

    cohorts.map(c => {
      // assign colors in reverse order - assumes that the cohorts are in some meaningful order
      if (hasFlipAxis) {
        c.color = getBrewerColor(palette, nc, ind--);
      } else {
        c.color = getBrewerColor(palette, nc, ind++);
      }
      c.pattern = 'solid';
      colorsUsed[c.color] = 1;
      cohortUpdates.push({
        cohort_id: c.id,
        color: c.color,
        pattern: c.pattern,
      });
    });
  });
  return cohortUpdates;
}
